import React, { useState } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { formatMessage } from '../../../translations/format-message';
import img404 from '../../../assets/Images/no_image.jpg';
import BusyGif from '../../../assets/Images/busy.gif';
import { CloseModalIcon } from '../../../shared-components/svgs/svgs';
import { FormattedMessage } from 'react-intl';

interface CorrectionRequestModalProps {
  show: boolean;
  onHide: () => void;
  onComplete: () => void;
  productId?: string | number;
  processName?: string;
  modificationDetails?: string;
  imagePath?: string;
  fileType?: string;
  isLoading?: boolean;
  isSubmitting?: boolean;
  apiData?: any;
}

// Shape of each entry returned in Data.CorrectionList by the
// GetCorrectionByProductProcessId API.
interface ICorrectionListItem {
  ID?: number;
  ProductID?: number;
  ProductName?: string;
  ProcessId?: number;
  ProcessName?: string;
  ModificationMessage?: string;
  Title?: string;
  S3FilePath?: string[];
}

const CorrectionRequestModal: React.FC<CorrectionRequestModalProps> = ({
  show,
  onHide,
  onComplete,
  productId = '',
  processName = '',
  modificationDetails = '',
  imagePath = '',
  fileType = '',
  isLoading = false,
  isSubmitting = false,
  apiData = null,
}) => {
  
  // API now returns Data.CorrectionList as an array of correction entries
  // (each with its own Title / ModificationMessage / S3FilePath), instead
  // of the previous single object with plain string fields.
  const responseData = apiData?.Data || apiData?.data;

  const correctionList: ICorrectionListItem[] = Array.isArray(responseData?.CorrectionList)
    ? responseData.CorrectionList
    : Array.isArray(responseData)
      ? responseData
      : responseData
        ? [responseData]
        : [];

  const isNoRecord = Boolean(
    apiData &&
    (apiData.StatusCode === 204 ||
      apiData.Message === 'NoRecordFound' ||
      responseData === null ||
      responseData === undefined ||
      correctionList.length === 0)
  );

  const firstItem = correctionList[0];

  const displayProductName =
    firstItem?.ProductName ??
    (firstItem as any)?.ProductId ??
    firstItem?.ProductID ??
    productId;

  // Do not touch process name: always use processName passed from Step 4 / Process tab
  const displayProcessName = processName;

  // One "TITLE - Modification Message" line per correction entry.
  const modificationLines: Array<{ key: string | number; title: string; message: string }> = isNoRecord
    ? []
    : correctionList.length > 0
      ? correctionList
          .filter((item) => item?.Title || item?.ModificationMessage)
          .map((item, idx) => ({
            key: item?.ID ?? idx,
            title: item?.Title || '-',
            message: item?.ModificationMessage || '-',
          }))
      : !apiData && modificationDetails
        ? [{ key: 'legacy', title: '', message: modificationDetails }]
        : [];

  const validFileItems: Array<{ url: string; fileType?: string; name?: string; title?: string }> = [];
  if (!isNoRecord) {
    correctionList.forEach((item) => {
      const itemTitle = item?.Title || '';
      const paths = Array.isArray(item?.S3FilePath)
        ? item.S3FilePath
        : item?.S3FilePath
          ? [item.S3FilePath]
          : [];

      paths.forEach((pathItem: any) => {
        if (!pathItem) return;
        let url = '';
        let fType = fileType || '';
        let fName = '';

        if (typeof pathItem === 'string') {
          url = pathItem.trim();
        } else if (typeof pathItem === 'object') {
          url = pathItem.S3FilePath || pathItem.FilePath || pathItem.url || pathItem.ImagePath || '';
          fType = pathItem.FileType || pathItem.fileType || fType;
          fName = pathItem.FileName || pathItem.fileName || pathItem.Name || '';
        }

        if (
          url &&
          typeof url === 'string' &&
          url.trim() !== '' &&
          url !== 'null' &&
          url !== 'undefined'
        ) {
          validFileItems.push({
            url: url.trim(),
            fileType: fType,
            name: fName,
            title: itemTitle,
          });
        }
      });
    });

    if (!apiData && validFileItems.length === 0 && imagePath) {
      validFileItems.push({ url: imagePath, fileType, name: '' });
    }
  }

  const displayFileType = fileType;

  return (
    <Modal
      className="correction-request-modal fancy-modal"
      backdropClassName="correction-modal-backdrop"
      show={show}
      onHide={onHide}
      centered
      size="xl"
    >
      <Modal.Header>
        <Modal.Title>
          {formatMessage('correction_request')}
          <button
            onClick={onHide}
            disabled={isLoading}
            className="Crossicons"
            type="button"
          >
            <CloseModalIcon />
          </button>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: '380px' }}>
            <img src={BusyGif} alt="loading..." style={{ width: '60px' }} />
          </div>
        ) : (
          <div className="correction-request-content row">
            <div className="col-12 col-md-5">
              <div className="row g-2">
                <div className="col-6 d-flex flex-column justify-content-start align-items-start mb-3">
                  <label className="form-label mb-0">
                    <FormattedMessage id="product_id" />
                  </label>
                  <p className="text-dark">
                    {displayProductName || '-'}
                  </p>
                </div>
                <div className="col-6 d-flex flex-column justify-content-start align-items-start mb-3">
                  <label className="form-label mb-0">
                    {formatMessage('correction_process')}
                  </label>
                  <p className="text-dark">
                    {displayProcessName || formatMessage('process_name')}
                  </p>
                </div>
                <div className="col-12">
                  <label className="form-label mb-0">
                    {formatMessage('modification_details')}
                  </label>
                  <div className="border rounded p-2 bg-light text-dark modification-details">
                    {modificationLines.length > 0 ? (
                      modificationLines.map((line) => (
                        <p key={line.key} className="mb-1">
                          {line.title && (
                            <span className="fw-bold text-dark d-block">{line.title}</span>
                          )}
                          
                          <small>{line.message}</small>
                        </p>
                      ))
                    ) : (
                      <p className="mb-0">-</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-7">
              <div className="mb-3">
                {validFileItems.length > 0 ? (
                  <div
                    className="border rounded p-3 bg-light d-flex flex-wrap gap-3 justify-content-center overflow-auto"
                    style={{ height: '500px' }}
                  >
                    {validFileItems.map((item, idx: number) => {
                      const cleanUrl = item.url.split('?')[0].toLowerCase();
                      const hasKnownExtension = /\.[a-z0-9]{2,5}$/.test(cleanUrl);
                      const isItemPdf =
                        item.fileType === 'application/pdf' ||
                        cleanUrl.endsWith('.pdf') ||
                        // Only fall back to the shared/derived file type when this
                        // item has no type or extension of its own to go by -
                        // never let it override a file's own (non-pdf) identity.
                        (!item.fileType &&
                          !hasKnownExtension &&
                          displayFileType === 'application/pdf');

                      return isItemPdf ? (
                        <div key={idx} className="position-relative w-100 text-center">
                          {item.title && (
                            <span className="badge bg-dark position-absolute top-0 end-0 m-1 text-truncate" style={{ maxWidth: '80%', zIndex: 1 }}>
                              {item.title}
                            </span>
                          )}
                          <iframe
                            src={`${item.url}#toolbar=0`}
                            title={item.name || `PDF Viewer ${idx}`}
                            style={{ border: 'none', width: '100%', height: '320px', maxWidth: '600px' }}
                          />
                        </div>
                      ) : (
                        <div key={idx} className="position-relative text-center">
                          {item.title && (
                            <span className="badge bg-dark position-absolute top-0 end-0 m-1 text-truncate" style={{ maxWidth: '80%', zIndex: 1 }}>
                              {item.title}
                            </span>
                          )}
                          <img
                            src={item.url}
                            alt={item.name || `${formatMessage('picture_label')}-${idx}`}
                            draggable={false}
                            className="img-fluid rounded border shadow-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="border rounded p-2 bg-light d-flex justify-content-center align-items-center overflow-hidden"
                    style={{ minHeight: '220px', maxHeight: '380px' }}
                  >
                    <img
                      src={img404}
                      alt={formatMessage('picture_label')}
                      className="img-fluid"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-end">
         <Button
            variant="light-danger"
            type="button"
            onClick={onHide}
            disabled={isLoading}
          >
            <FormattedMessage id='cancel' />
          </Button>
        <Button variant="primary" className="px-4" onClick={onComplete} disabled={isLoading || isSubmitting}>
          {isSubmitting ? formatMessage('loading') || 'Loading...' : formatMessage('completion')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CorrectionRequestModal;
